// FiveSForm (Tailwind Refactored)

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import Loader from '../../components/utils/Loader';
import useFetch from '../../hooks/useFetch';
import AILightComponent from '../../components/AILightComponent';
import DatePicker from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';

const FiveSForm = () => {
  const authState = useSelector((state) => state.authReducer);
  const navigate = useNavigate();
  const { pageId } = useParams();

  const [fetchData, { loading }] = useFetch();
  const mode = pageId === undefined ? "add" : "update";
  const itemId = pageId;

  const [formErrors, setFormErrors] = useState({});
  const [isUpdateLock, setIsUpdateLock] = useState(false);
  const [isCheckedPageContentAISupport, setIsCheckedPageContentAISupport] = useState(false);

  const [formData, setFormData] = useState({
    userId: authState.user._id,
    userCode: authState.user.think5whyscode,
    userName: authState.user.name,
    fiveSCat: "HOM",
    title: "",
    fiveSDate: new Date(),
    pageNumber: "",
    pageContent: "",
    s1: "",
    s2: "",
    s3: "",
    s4: "",
    s5: "",
    comments: "",
    isActive: true,
    isDeleted: false,
    dateCreated: new Date(),
    createdBy: authState.user._id,
  });

  useEffect(() => {
    document.title = mode === "add" ? "Add" : "Update";
  }, [mode]);

  useEffect(() => {
    if (mode === "update" && itemId) {
      const config = {
        url: "/fives/getforupdate",
        method: "post",
        headers: { Authorization: authState.token },
        data: { itemId },
      };

      fetchData(config, { showSuccessToast: false })
        .then((data) => {
          const f = data.fiveS;

          setFormData((prev) => ({
            ...prev,
            ...f,
            fiveSDate: f.fiveSDate ? new Date(f.fiveSDate) : new Date(),
            dateCreated: f.dateCreated || prev.dateCreated,
            createdBy: f.createdBy || prev.createdBy,
          }));
        })
        .catch(() => {
          console.error("Error fetching FiveS data");
        });
    }
  }, [mode, itemId, fetchData, authState.token]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validateForm = (fd) => {
    const e = {};
    const check = (field, label, max = 500) => {
      if (!fd[field]) e[field] = `${label} is required`;
      else if (fd[field].length > max) e[field] = `${label} cannot exceed ${max} characters`;
    };

    check("title", "Title", 100);
    check("s1", "Sort");
    check("s2", "Set in Order");
    check("s3", "Shine");
    check("s4", "Standardize");
    check("s5", "Sustain");

    if (fd.comments && fd.comments.length > 500)
      e.comments = "Comments cannot exceed 500 characters";

    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validateForm(formData);
    setFormErrors(err);

    if (Object.keys(err).length > 0) return;

    const config =
      mode === "add"
        ? {
            url: "/fives/save",
            method: "post",
            data: { formData, user: authState.user._id },
            headers: { Authorization: authState.token },
          }
        : {
            url: `/fives/${itemId}`,
            method: "put",
            data: formData,
            headers: { Authorization: authState.token },
          };

    fetchData(config).then(() => navigate("/fives-mylist"));
  };

  const fieldError = (field) =>
    formErrors[field] && (
      <p className="mt-1 text-pink-500 text-xs flex items-center">
        <i className="fa-solid fa-circle-exclamation mr-2"></i>
        {formErrors[field]}
      </p>
    );

  return (
    <div className="bg-black">
      <div className="min-h-full py-4 bg-black text-white rounded-lg px-3">
        
        <div className='border-b border-blue-400'>
          <h2 className="text-xl lg:text-2xl py-2 px-4 text-yellow-200">🧹 5S Task Entries</h2>
        </div>

        {loading ? (
          <Loader />
        ) : (
          <div className="text-sm space-y-6">

            <div className="flex justify-start px-4 py-4">
              <h2 className="tet-lg lg:text-xl text-yellow-300">
                {mode === "add" ? "Create 5S" : "Edit 5S"}
              </h2>
            </div>

            {/* Form Block */}
            <div className="border border-gray-600 rounded-md p-3 space-y-3 space-x-4">

              {/* Category */}
              <div className='space-x-4'> 
                <label className="ml-4">Choose Category*</label>
                <select
                  name="fiveSCat"
                  value={formData.fiveSCat}
                  onChange={handleChange}
                  className="h-10 mt-1 w-40 p-2 bg-black text-white border border-gray-300 rounded-md focus:ring focus:ring-yellow-400"
                >
                  <option value="Business">Business</option>
                  <option value="Education">Education</option>
                  <option value="Government">Government</option>
                  <option value="Family">Family</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="NGO">NGO</option>
                  <option value="Profession">Profession</option>
                  <option value="Others">Others</option>
                </select>
                {fieldError("fiveSCat")}
              </div>

              {/* Page Number */}
              {/* <div>
                <label>Page#*</label>
                <input
                  type="number"
                  name="pageNumber"
                  value={formData.pageNumber}
                  onChange={handleChange}
                  min={1}
                  max={502}
                  className="w-20 p-2 bg-black text-white border border-gray-300 rounded-md"
                />
                {fieldError("pageNumber")}
              </div> */}

              {/* Date */}
              <div className='space-x-4'> 
                <label>Date*</label>
                <DatePicker
                  selected={formData.fiveSDate}
                  onChange={(date) =>
                    setFormData((prev) => ({ ...prev, fiveSDate: date }))
                  }
                  dateFormat="dd/MM/yyyy"
                  className="w-full h-9 p-2 border border-gray-300 rounded-md bg-black text-white"
                />
                {fieldError("fiveSDate")}
              </div>

              {/* Title */}
              <div className='space-x-4'> 
                <label>Title*</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full p-2 bg-black text-white border border-gray-300 rounded-md"
                />
                {fieldError("title")}
              </div>

              {/* 5S Fields */}
              {[
                ["s1", "Sort (Seiri)*"],
                ["s2", "Set in Order (Seiton)*"],
                ["s3", "Shine (Seiso)*"],
                ["s4", "Standardize (Seiketsu)*"],
                ["s5", "Sustain (Shitsuke)*"],
              ].map(([id, label]) => (
                <div key={id}>
                  <label>{label}</label>
                  <textarea
                    name={id}
                    value={formData[id]}
                    onChange={handleChange}
                    maxLength="500"
                    className="w-full h-28 p-2 bg-black text-white border border-gray-300 rounded-md"
                  />
                  {fieldError(id)}
                </div>
              ))}

              {isCheckedPageContentAISupport && formData.pageContent !== "" && (
                <AILightComponent
                  param1="Page Content"
                  param2={formData.pageContent}
                />
              )}
            </div>

            {/* Comments */}
            <div className="border border-gray-600 rounded-md p-3">
              <label>Comment/Remarks</label>
              <textarea
                name="comments"
                value={formData.comments}
                onChange={handleChange}
                placeholder="Enter FiveS Remarks..."
                className="w-full h-28 p-2 bg-black text-white border border-gray-300 rounded-md"
              />
              {fieldError("comments")}
            </div>

            {/* Buttons */}
            <div className="border border-gray-600 rounded-md p-4 flex flex-col items-center space-y-3">

              {Object.keys(formErrors).length > 0 && (
                <div className="text-red-300 text-center space-y-1">
                  {Object.values(formErrors).map((msg, i) => (
                    <div key={i}>{msg}</div>
                  ))}
                </div>
              )}

              <div className="flex space-x-4">
                <button
                  onClick={handleSubmit}
                  disabled={isUpdateLock && mode === "update"}
                  className="bg-blue-600 px-4 py-2 rounded-md text-white hover:bg-blue-700"
                >
                  {mode === "add" ? "Add New" : "Update"}
                </button>

                <button
                  onClick={() => navigate("/fives-mylist")}
                  className="bg-red-600 px-4 py-2 rounded-md text-white hover:bg-red-700"
                >
                  Cancel
                </button>
              </div>

            </div>

          </div>
        )}
      </div>
      <footer className="bg-[#2a0f2f] border-t border-blue-800 text-blue-300 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-xs lg:text-sm">
            © 2025 Think5Whys Global DeepTech Solutions.
          </div>
          <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
            <Link to="/about-us" className="text-white hover:text-blue-400">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-blue-400">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-blue-400">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-blue-400">Contact</Link>
          </div>
        </div>
      </footer>          
    </div>
  );
};

export default FiveSForm;
